import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Database, Lock, Eye, Globe, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const fadeInUpStagger = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] } }
});

const Privacy = () => {
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);

  // Section refs
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const highlightsRef = useRef(null);
  const contentRef = useRef(null);
  const ctaRef = useRef(null);

  // InView hooks
  const headerInView = useInView(headerRef, { amount: 0.05, once: true });
  const navInView = useInView(navRef, { amount: 0.05, once: true });
  const highlightsInView = useInView(highlightsRef, { amount: 0.05, once: true });
  const contentInView = useInView(contentRef, { amount: 0.05, once: true });
  const ctaInView = useInView(ctaRef, { amount: 0.05, once: true });

  const scrollToSection = (sectionId: string) => {
    // SSR-safe: Only execute on client-side
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setHighlightedSection(sectionId);
      setTimeout(() => setHighlightedSection(null), 2000);
    }
  };

  return (
    <div className="h-full w-full text-foreground overflow-hidden transition-colors duration-300 landing-gradient-bg">

      {/* Animated Grid Background */}

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="pt-32 pb-16 px-8 md:px-16 xl:px-32"
      >
        <div className="max-w-screen-2xl mx-auto">
          {/* Header */}
          <motion.div ref={headerRef} variants={fadeInUp} initial="hidden" animate={headerInView ? 'visible' : 'hidden'} className="mb-12 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Shield className="h-10 w-10 text-blue-600" />
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
            </div>
            <p className="text-xl text-black dark:text-gray-300">Your privacy and data protection at Synkro</p>
          </motion.div>

          {/* Quick Navigation */}
          <motion.div ref={navRef} variants={fadeInUp} initial="hidden" animate={navInView ? 'visible' : 'hidden'} className="bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-base">
              <button onClick={() => scrollToSection('collection')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Data Collection</button>
              <button onClick={() => scrollToSection('usage')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">How We Use Data</button>
              <button onClick={() => scrollToSection('sharing')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Data Sharing</button>
              <button onClick={() => scrollToSection('security')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Security</button>
              <button onClick={() => scrollToSection('rights')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Your Rights</button>
              <button onClick={() => scrollToSection('children')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Children's Privacy</button>
              <button onClick={() => scrollToSection('contact')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Contact Us</button>
            </div>
          </motion.div>

          {/* Privacy Highlights */}
          <motion.div ref={highlightsRef} variants={fadeInUp} initial="hidden" animate={highlightsInView ? 'visible' : 'hidden'} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <motion.div variants={fadeInUpStagger(0.1)} className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No Data Sales</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">We never sell your personal data to third parties</p>
            </motion.div>
            <motion.div variants={fadeInUpStagger(0.2)} className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 text-center">
              <Lock className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">End-to-End Encryption</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Your data is encrypted in transit and at rest</p>
            </motion.div>
            <motion.div variants={fadeInUpStagger(0.3)} className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 text-center">
              <Eye className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Full Transparency</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Clear explanations of how we handle your data</p>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div ref={contentRef} variants={fadeInUp} initial="hidden" animate={contentInView ? 'visible' : 'hidden'} className="space-y-8">
            
            <motion.div id="collection" variants={fadeInUpStagger(0.1)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'collection' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-900/20 rounded-lg p-2">
                  <Database className="h-6 w-6 text-blue-600" />
                </div>
                Information We Collect
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Account Information</h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Email address</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Name and profile information</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Account preferences</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Research Data</h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Project files and documents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Notes and annotations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">AI-generated summaries</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Usage Information</h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-purple-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Feature usage patterns</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-purple-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Performance analytics</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-purple-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Error logs and debugging data</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Google Services Data</h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-orange-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Calendar events (with permission)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-orange-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Drive files (with permission)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-orange-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Contact information (with permission)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div id="usage" variants={fadeInUpStagger(0.2)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'usage' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/20 rounded-lg p-2">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                How We Use Your Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Provide Services</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Deliver core research and project management features</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">AI Enhancement</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Generate summaries, insights, and recommendations</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Account Management</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Maintain your account and preferences</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Service Improvement</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Analyze usage to enhance our platform</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Communication</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Send updates, notifications, and support messages</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Security & Compliance</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Protect against fraud and ensure platform security</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div id="sharing" variants={fadeInUpStagger(0.3)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'sharing' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-red-100 dark:bg-red-900/20 rounded-lg p-2">
                  <Globe className="h-6 w-6 text-red-600" />
                </div>
                Data Sharing and Disclosure
              </h2>
              
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-200">We do NOT sell your data</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300">Your personal information is never sold to advertisers or third parties for commercial purposes.</p>
              </div>
              
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Service Providers</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">We may share data with trusted service providers who help us operate our platform:</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-blue-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">Supabase (database and authentication)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-blue-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">Google Services (with your explicit permission)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-blue-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">AI providers (for processing and analysis)</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Legal Requirements</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">We may disclose information when required by law or to protect our rights and users' safety.</p>
                </div>
              </div>
            </motion.div>

            <motion.div id="security" variants={fadeInUpStagger(0.4)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'security' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-purple-100 dark:bg-purple-900/20 rounded-lg p-2">
                  <Lock className="h-6 w-6 text-purple-600" />
                </div>
                Security Measures
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Encryption</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">All data is encrypted in transit using TLS and at rest using AES-256 encryption.</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Access Controls</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Strict access controls ensure only authorized personnel can access your data.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Regular Audits</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">We conduct regular security audits and vulnerability assessments.</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Incident Response</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">24/7 monitoring and rapid response to any security incidents.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div id="rights" variants={fadeInUpStagger(0.5)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'rights' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-indigo-100 dark:bg-indigo-900/20 rounded-lg p-2">
                  <Eye className="h-6 w-6 text-indigo-600" />
                </div>
                Your Privacy Rights
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                  <Database className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Access</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Request a copy of your personal data</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Portability</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Export your data in common formats</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                  <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Deletion</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Request deletion of your account and data</p>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">To exercise any of these rights, please contact us using the information below.</p>
                <Button asChild>
                  <Link to="/contact">Contact Privacy Team</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div id="children" variants={fadeInUpStagger(0.6)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'children' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-yellow-100 dark:bg-yellow-900/20 rounded-lg p-2">
                  <Shield className="h-6 w-6 text-yellow-600" />
                </div>
                Children's Privacy
              </h2>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Age Restriction</h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Synkro is not intended for children under 13 years of age. We do not knowingly collect personal 
                      information from children under 13. If you believe we have collected information from a child under 13, 
                      please contact us immediately.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div id="contact" variants={fadeInUpStagger(0.7)} className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'contact' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-gray-100 dark:bg-gray-900/20 rounded-lg p-2">
                  <Users className="h-6 w-6 text-gray-600" />
                </div>
                Contact Us About Privacy
              </h2>
              
              <div className="text-center space-y-4">
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  If you have questions about this Privacy Policy or your privacy rights, please contact us:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Privacy Officer</h4>
                    <p className="text-blue-600 dark:text-blue-400">support@synkroai.app</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Business Address</h4>
                    <p className="text-purple-600 dark:text-purple-400">
                      Building 32<br />
                      Fidar, Byblos<br />
                      Lebanon
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CTA Section */}
            <motion.div ref={ctaRef} variants={fadeInUp} initial="hidden" animate={ctaInView ? 'visible' : 'hidden'} className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl text-white text-center">
              <p className="text-blue-100 mb-4">
                <strong>Ready to experience secure research?</strong> Your privacy is protected with Synkro.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/terms">
                  <Button
                    variant="ghost"
                    size="lg"
                    className="relative bg-black/5 dark:bg-white/5 backdrop-blur-md hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    <span className="relative z-10 font-medium">
                      View Terms of Service
                    </span>
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button
                    variant="ghost"
                    size="lg"
                    className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    <span className="relative z-10 font-medium">
                      Get Started Securely
                    </span>
                  </Button>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll to Top Button */}
    </div>
  );
};

export default Privacy; 